import React from "react";
import { Button, Chip, Popover } from "@narmi/design_system";

import FilterCheckbox from "../functional/FilterCheckbox";
import { BYZANTINE_COPY_TO_FLUENT_ID } from "./OrganizationUsers.constants";

import type {
  FilterFunction,
  FilterType,
  DisplayFilter,
} from "./OrganizationUsers.types";

interface FilterPopoverContentProps {
  displayFilters: DisplayFilter[];
  pendingFilters: FilterFunction[];
  setPendingFilters: (
    updater: (prev: FilterFunction[]) => FilterFunction[]
  ) => void;
  filterType: FilterType;
  onClose?: () => void;
  filters: FilterFunction[];
  toggleFilter: (filter: FilterFunction) => void;
  setFilteringByRole: (value: boolean) => void;
  setFilteringByStatus: (value: boolean) => void;
  l10n: any;
}

const FilterPopoverContent: React.FC<FilterPopoverContentProps> = ({
  displayFilters,
  pendingFilters,
  setPendingFilters,
  filterType,
  onClose,
  filters,
  toggleFilter,
  setFilteringByRole,
  setFilteringByStatus,
  l10n,
}) => (
  <div style={{ padding: "8px" }}>
    <div
      style={{
        width: "213px",
        display: "flex",
        flexDirection: "column",
        gap: 0,
      }}
    >
      {displayFilters.map(({ text, filter }) => (
        <div
          key={text}
          role="button"
          tabIndex={0}
          onClick={() => {
            setPendingFilters((prev) =>
              prev.includes(filter)
                ? prev.filter((f) => f !== filter)
                : [...prev, filter]
            );
          }}
          onKeyUp={({ key }) => {
            if (key === "Enter") {
              setPendingFilters((prev) =>
                prev.includes(filter)
                  ? prev.filter((f) => f !== filter)
                  : [...prev, filter]
              );
            }
          }}
          style={{ cursor: "pointer" }}
        >
          <FilterCheckbox
            label={l10n.getString(BYZANTINE_COPY_TO_FLUENT_ID[text])}
            filters={pendingFilters}
            toggleFilter={() => {
              setPendingFilters((prev) =>
                prev.includes(filter)
                  ? prev.filter((f) => f !== filter)
                  : [...prev, filter]
              );
            }}
            filter={filter}
          />
        </div>
      ))}
    </div>
    <hr
      style={{
        margin: "8px 0",
        border: "none",
        borderTop: "1px solid rgb(var(--border-color))",
      }}
    />
    <div
      style={{
        padding: "8px",
        display: "flex",
        gap: "8px",
        justifyContent: "space-between",
      }}
    >
      <Button
        kind="negative"
        label={l10n.getString("org-user-filter-remove")}
        onClick={() => {
          displayFilters.forEach(({ filter }) => {
            if (filters.includes(filter)) {
              toggleFilter(filter);
            }
          });
          if (filterType === "ROLE") setFilteringByRole(false);
          if (filterType === "STATUS") setFilteringByStatus(false);
          onClose?.();
        }}
        size="xs"
      />
      <Button
        kind="primary"
        label={l10n.getString("org-user-filter-apply")}
        onClick={() => {
          const nonTypeFilters = filters.filter(
            (f) => !displayFilters.some((df) => df.filter === f)
          );
          const newFilters = [...nonTypeFilters, ...pendingFilters];
          newFilters.forEach((filter) => {
            if (!filters.includes(filter)) {
              toggleFilter(filter);
            }
          });
          filters.forEach((filter) => {
            if (
              !newFilters.includes(filter) &&
              displayFilters.some((df) => df.filter === filter)
            ) {
              toggleFilter(filter);
            }
          });
          onClose?.();
        }}
        size="xs"
      />
    </div>
  </div>
);

export default FilterPopoverContent;

export interface FilterChipProps {
  displayFilters: DisplayFilter[];
  pendingFilters: FilterFunction[];
  setPendingFilters: (
    updater: (prev: FilterFunction[]) => FilterFunction[]
  ) => void;
  filterType: FilterType;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  label: string;
  filters: FilterFunction[];
  toggleFilter: (filter: FilterFunction) => void;
  setFilteringByRole: (value: boolean) => void;
  setFilteringByStatus: (value: boolean) => void;
  l10n: any;
  getFilterCount: (filters: FilterFunction[], displayFilters: DisplayFilter[]) => number | undefined;
}

export const FilterChip: React.FC<FilterChipProps> = ({
  displayFilters,
  pendingFilters,
  setPendingFilters,
  filterType,
  isOpen,
  setIsOpen,
  label,
  filters,
  toggleFilter,
  setFilteringByRole,
  setFilteringByStatus,
  l10n,
  getFilterCount,
}) => (
  <div className="org-users-left-aligned-popover" style={{ display: "inline-block" }}>
    <Popover
      content={
        <FilterPopoverContent
          displayFilters={displayFilters}
          pendingFilters={pendingFilters}
          setPendingFilters={setPendingFilters}
          filterType={filterType}
          onClose={() => setIsOpen(false)}
          filters={filters}
          toggleFilter={toggleFilter}
          setFilteringByRole={setFilteringByRole}
          setFilteringByStatus={setFilteringByStatus}
          l10n={l10n}
        />
      }
      wrapperDisplay="inline"
      onUserDismiss={() => setIsOpen(false)}
    >
      <Chip
        kind="secondary"
        endIcon={isOpen ? "chevron-up" : "chevron-down"}
        label={label}
        count={getFilterCount(filters, displayFilters)}
        onClick={() => setIsOpen(!isOpen)}
      />
    </Popover>
  </div>
);
